import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ComplianceCoach from "@/components/ComplianceCoach";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

const CAPA_STATES = ["Open", "Investigation", "RootCause", "ActionPlan", "Implementation", "Effectiveness", "Closed"];
const STATE_COLORS = {
  Open: "bg-amber-50 text-amber-700 border-amber-200",
  Investigation: "bg-blue-50 text-blue-700 border-blue-200",
  RootCause: "bg-indigo-50 text-indigo-700 border-indigo-200",
  ActionPlan: "bg-violet-50 text-violet-700 border-violet-200",
  Implementation: "bg-cyan-50 text-cyan-700 border-cyan-200",
  Effectiveness: "bg-orange-50 text-orange-700 border-orange-200",
  Closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function CAPA() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", source: "Internal", severity: "Medium" });

  const load = async () => {
    const { data } = await api.get("/capa");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.description) return toast.error("Fill required fields");
    try {
      await api.post("/capa", form);
      toast.success("CAPA created");
      setOpen(false);
      setForm({ title: "", description: "", source: "Internal", severity: "Medium" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const filtered = list.filter((c) => !q ||
    c.title.toLowerCase().includes(q.toLowerCase()) || c.capa_id.includes(q));

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="vq-overline mb-2">Quality Events</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">CAPA</h1>
            <p className="text-slate-600 mt-2">Corrective & Preventive Action — ISO 13485 §8.5.2 / 21 CFR 820.100</p>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="capa-new-btn" className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> New CAPA
          </Button>
        </div>

        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search CAPAs…" className="pl-9 bg-white" data-testid="capa-search" />
        </div>

        <div className="vq-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">CAPA</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Severity</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Source</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">State</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Owner</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-16 text-slate-500">No CAPAs. Create one to get started.</td></tr>
              ) : filtered.map((c) => (
                <tr key={c.capa_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <Link to={`/capa/${c.capa_id}`} data-testid={`capa-row-${c.capa_id}`}>
                      <div className="font-display font-bold text-slate-900">{c.title}</div>
                      <div className="text-xs text-slate-500 font-mono">{c.capa_id}</div>
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm">{c.severity}</td>
                  <td className="px-6 py-4 text-sm">{c.source}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-[10px] font-mono border ${STATE_COLORS[c.state] || ""}`}>{c.state}</span>
                  </td>
                  <td className="px-6 py-4 text-xs font-mono text-slate-500">{c.owner_email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">New CAPA</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold">Title</Label>
              <Input data-testid="capa-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Description</Label>
              <Textarea data-testid="capa-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Internal", "Audit", "Complaint", "Deviation", "Supplier"].map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Low", "Medium", "High", "Critical"].map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <ComplianceCoach entityType="capa" draft={form} testIdPrefix="capa-coach" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="capa-form-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
